/* Patch 17 — Leads Console + Conversion Wizard
   ================================================
   Lists landing-page Leads with conversion status, opens a
   4-step Conversion Wizard:
     Step 1: Lead Review (read-only)
     Step 2: Select / Create Customer
     Step 3: Add Items + Pricing
     Step 4: Confirm + Conversion Notes  →  Create Quotation

   Also renders a per-Lead Timeline (creation, audit entries,
   quotations, projects). */
import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Users, ArrowRight, ArrowLeft, X, Plus, Trash2,
  CheckCircle2, FileText, Clock, AlertCircle,
} from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);
const plus30 = () => {
  const d = new Date(); d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

export default function LeadsConsole() {
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState({}); // lead_id → conversion_status
  const [loading, setLoading] = useState(false);
  const [wizardLead, setWizardLead] = useState(null);
  const [timelineLead, setTimelineLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/leads/recent?limit=100');
      const items = r.data?.items || [];
      setLeads(items);
      // Lazy-load conversion-status per lead (batched)
      const next = {};
      await Promise.all(items.map(async (l) => {
        try {
          const s = await axiosInstance.get(`/leads/${l.id}/conversion-status`);
          next[l.id] = s.data;
        } catch { /* tenant-scope may 403 on some */ }
      }));
      setStatuses(next);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = leads.reduce((acc, l) => {
    const st = statuses[l.id]?.state || 'none';
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="leads-console-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Users className="text-sky-600" size={26} /> Leads → Quotation
          </h1>
          <p className="text-sm text-slate-600">
            Convert landing-page leads into priced quotations with full source traceability.
          </p>
        </div>
        <button
          data-testid="leads-refresh-btn"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-4 gap-3" data-testid="leads-summary-cards">
        <SummaryCard label="Total Leads"  value={leads.length} cls="text-slate-900" />
        <SummaryCard label="Not Converted" value={counts.none || 0} cls="text-amber-700" />
        <SummaryCard label="Converted"    value={counts.converted || 0} cls="text-emerald-700" />
        <SummaryCard label="Alternatives" value={counts.alternative || 0} cls="text-sky-700" />
      </div>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs" data-testid="leads-table">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <th className="px-3 py-2">Lead</th>
              <th className="px-3 py-2">Name / Email</th>
              <th className="px-3 py-2">Company</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Conversion</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-500">No leads.</td></tr>
            ) : (
              leads.map((l, i) => {
                const s = statuses[l.id];
                const state = s?.state || 'none';
                const badgeCls = {
                  none:        'bg-amber-100 text-amber-800',
                  converted:   'bg-emerald-100 text-emerald-800',
                  alternative: 'bg-sky-100 text-sky-800',
                }[state] || 'bg-slate-100';
                return (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{l.id?.slice(-8)}</td>
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900">{l.name}</div>
                      <div className="text-[10px] text-slate-500">{l.email}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 truncate max-w-[150px]">{l.company || '—'}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-500">{(l.created_at || '').slice(0, 10)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeCls}`}>
                        {state.toUpperCase()}
                      </span>
                      {s?.quotation_count > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {s.quotations.map(q => q.quotation_no).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        data-testid={`lead-convert-${i}`}
                        onClick={() => setWizardLead(l)}
                        className="inline-flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white text-[10px] font-semibold rounded px-2 py-1 mr-1"
                      >
                        {state === 'none' ? 'Convert' : 'Alternative'} <ArrowRight size={9} />
                      </button>
                      <button
                        data-testid={`lead-timeline-${i}`}
                        onClick={() => setTimelineLead(l)}
                        className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-semibold rounded px-2 py-1"
                      >
                        <Clock size={9} /> Timeline
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      {wizardLead && (
        <ConversionWizard
          lead={wizardLead}
          isAlternative={(statuses[wizardLead.id]?.state || 'none') !== 'none'}
          onClose={() => setWizardLead(null)}
          onSuccess={async () => { setWizardLead(null); await load(); }}
        />
      )}

      {timelineLead && (
        <TimelineDrawer
          lead={timelineLead}
          onClose={() => setTimelineLead(null)}
        />
      )}
    </div>
  );
}


function SummaryCard({ label, value, cls }) {
  return (
    <div className="bg-white rounded-lg p-3 border border-slate-200">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-3xl font-bold font-mono mt-1 ${cls}`}>{value}</div>
    </div>
  );
}


function ConversionWizard({ lead, isAlternative, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([
    { description: lead.message?.slice(0, 80) || '', quantity: 1, unit_price: 0, unit: 'ls', total: 0 },
  ]);
  const [quotationDate, setQuotationDate] = useState(today());
  const [validDate, setValidDate] = useState(plus30());
  const [vatRate, setVatRate] = useState(15);
  const [notes, setNotes] = useState('');
  const [conversionNotes, setConversionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axiosInstance.get('/customers').then(r => setCustomers(r.data || []))
      .catch(() => toast.error('Failed to load customers'));
  }, []);

  const subtotal = items.reduce(
    (s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const vatAmt = +(subtotal * vatRate / 100).toFixed(2);
  const total = +(subtotal + vatAmt).toFixed(2);

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx
      ? { ...it, ...patch, total: ((patch.quantity ?? it.quantity) * (patch.unit_price ?? it.unit_price)) || 0 }
      : it));
  };
  const addItem = () => setItems([...items, { description: '', quantity: 1, unit_price: 0, unit: 'ls', total: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axiosInstance.post(
        `/leads/${lead.id}/convert-to-quotation`,
        {
          customer_id: customerId,
          items,
          quotation_date: quotationDate,
          valid_date: validDate,
          vat_rate: vatRate,
          notes,
          conversion_notes: conversionNotes,
          force_alternative: isAlternative,
        });
      toast.success(`Created ${r.data.quotation_no} ✓`);
      await onSuccess();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Convert failed: ${typeof d === 'object' ? (d.error || JSON.stringify(d)) : (d || e.message)}`);
    } finally { setSubmitting(false); }
  };

  const stepValid = {
    1: true,
    2: !!customerId,
    3: items.length > 0 && items.every(it => it.description && it.quantity > 0 && it.unit_price >= 0),
    4: true,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="conversion-wizard">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500">
              {isAlternative ? 'Create Alternative Quotation' : 'Convert Lead to Quotation'}
            </div>
            <h2 className="text-base font-bold text-slate-900">{lead.name} — {lead.email}</h2>
          </div>
          <div className="flex items-center gap-2">
            <StepDots step={step} />
            <button data-testid="wizard-close" onClick={onClose} className="p-2 hover:bg-slate-100 rounded">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="p-5">
          {step === 1 && (
            <div data-testid="wizard-step-1" className="space-y-3">
              <h3 className="text-sm font-bold">Step 1 of 4 — Lead Review</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Field label="Name"      value={lead.name} />
                <Field label="Email"     value={lead.email} />
                <Field label="Phone"     value={lead.phone || '—'} />
                <Field label="Company"   value={lead.company || '—'} />
                <Field label="Type"      value={lead.company_type || '—'} />
                <Field label="Source"    value={lead.source || 'landing_page'} />
              </div>
              {lead.message && (
                <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs">
                  <strong>Message:</strong> {lead.message}
                </div>
              )}
              {isAlternative && (
                <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900 inline-flex items-center gap-1">
                  <AlertCircle size={12} /> This lead already has a quotation.
                  Creating an alternative — for revisions use the quotation&apos;s Revise action.
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-2" className="space-y-3">
              <h3 className="text-sm font-bold">Step 2 of 4 — Select Customer</h3>
              <p className="text-xs text-slate-600">
                Pick an existing customer for this tenant. If the customer doesn&apos;t
                exist yet, create one via the Customers page first, then return here.
              </p>
              <select
                data-testid="wizard-customer-select"
                value={customerId}
                onChange={e => setCustomerId(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              >
                <option value="">— Select customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name_english || c.name_arabic} — {c.customer_number || ''}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quotation Date">
                  <input type="date" value={quotationDate} onChange={e => setQuotationDate(e.target.value)}
                    data-testid="wizard-quotation-date"
                    className="border border-slate-300 rounded px-2 py-1 text-xs w-full" />
                </Field>
                <Field label="Valid Until">
                  <input type="date" value={validDate} onChange={e => setValidDate(e.target.value)}
                    data-testid="wizard-valid-date"
                    className="border border-slate-300 rounded px-2 py-1 text-xs w-full" />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-3" className="space-y-3">
              <h3 className="text-sm font-bold">Step 3 of 4 — Items & Pricing</h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                    <th className="px-2 py-1">Description</th>
                    <th className="px-2 py-1 w-16">Qty</th>
                    <th className="px-2 py-1 w-12">Unit</th>
                    <th className="px-2 py-1 w-24">Unit Price</th>
                    <th className="px-2 py-1 w-24 text-right">Total</th>
                    <th className="px-2 py-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t border-slate-100" data-testid={`wizard-item-${idx}`}>
                      <td className="px-2 py-1">
                        <input value={it.description} onChange={e => updateItem(idx, { description: e.target.value })}
                          placeholder="Item description"
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={it.quantity} onChange={e => updateItem(idx, { quantity: +e.target.value })}
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input value={it.unit} onChange={e => updateItem(idx, { unit: e.target.value })}
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={it.unit_price} onChange={e => updateItem(idx, { unit_price: +e.target.value })}
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(it.total)}</td>
                      <td className="px-2 py-1">
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700">
                            <Trash2 size={11} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addItem} className="text-xs text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
                <Plus size={11} /> Add item
              </button>
              <div className="grid grid-cols-3 gap-3 mt-3 bg-slate-50 rounded p-3 text-xs">
                <Field label="Subtotal" value={fmt(subtotal)} mono />
                <Field label="VAT (%)">
                  <input type="number" value={vatRate} onChange={e => setVatRate(+e.target.value)}
                    className="border border-slate-300 rounded px-2 py-1 text-xs w-full" />
                </Field>
                <Field label="Total" value={fmt(total)} mono cls="font-bold text-slate-900" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="wizard-step-4" className="space-y-3">
              <h3 className="text-sm font-bold">Step 4 of 4 — Confirm</h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
                <div><strong>Customer:</strong> {customers.find(c => c.id === customerId)?.name_english || customerId}</div>
                <div><strong>Items:</strong> {items.length} · <strong>Total:</strong> {fmt(total)} SAR</div>
                <div><strong>Source:</strong> Lead {lead.id?.slice(-8)} — {lead.name}</div>
              </div>
              <Field label="Conversion Notes (optional)">
                <textarea value={conversionNotes} onChange={e => setConversionNotes(e.target.value)}
                  data-testid="wizard-conversion-notes"
                  rows={2} placeholder="Why this conversion now? Surfaces in the Lead timeline."                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs" />
              </Field>
              <Field label="Quotation Notes (optional)">
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  rows={2} className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs" />
              </Field>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} className="px-3 py-1.5 text-xs rounded border border-slate-300 inline-flex items-center gap-1">
              <ArrowLeft size={11} /> Back
            </button>
          )}
          {step < 4 ? (
            <button
              data-testid="wizard-next-btn"
              disabled={!stepValid[step]}
              onClick={() => setStep(step + 1)}
              className="px-3 py-1.5 text-xs rounded bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
            >
              Next <ArrowRight size={11} />
            </button>
          ) : (
            <button
              data-testid="wizard-create-btn"
              disabled={submitting}
              onClick={submit}
              className="px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
            >
              {submitting ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              {isAlternative ? 'Create Alternative' : 'Create Quotation'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}


function StepDots({ step }) {
  return (
    <div className="flex items-center gap-1" data-testid="wizard-step-dots">
      {[1,2,3,4].map(n => (
        <div key={n} className={`w-2 h-2 rounded-full ${
          n < step ? 'bg-emerald-500' : n === step ? 'bg-slate-900' : 'bg-slate-300'
        }`} />
      ))}
    </div>
  );
}


function Field({ label, value, children, mono, cls }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      {children
        ? <div className="mt-0.5">{children}</div>
        : <div className={`text-xs text-slate-900 ${mono ? 'font-mono' : ''} ${cls || ''}`}>{value || '—'}</div>}
    </div>
  );
}


function TimelineDrawer({ lead, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axiosInstance.get(`/leads/${lead.id}/timeline`)
      .then(r => setData(r.data))
      .catch(e => toast.error(`Timeline failed: ${e.message}`))
      .finally(() => setLoading(false));
  }, [lead.id]);

  const TYPE_META = {
    lead_created:                  { icon: Users, cls: 'text-sky-600' },
    converted_to_quotation:        { icon: ArrowRight, cls: 'text-emerald-600' },
    alternative_quotation_created: { icon: ArrowRight, cls: 'text-violet-600' },
    quotation_created:             { icon: FileText, cls: 'text-slate-600' },
    quotation_approved:            { icon: CheckCircle2, cls: 'text-emerald-600' },
    quotation_converted_to_invoice:{ icon: ArrowRight, cls: 'text-emerald-700' },
    project_created:               { icon: FileText, cls: 'text-indigo-600' },
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-timeline-drawer">
      <button className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500">Lead Timeline</div>
            <h2 className="text-base font-bold">{lead.name}</h2>
          </div>
          <button data-testid="timeline-close" onClick={onClose} className="p-2 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <Loader2 size={14} className="animate-spin text-slate-500" />}
          {data && (
            <ol className="relative border-l-2 border-slate-200 ml-2" data-testid="timeline-events">
              {data.events.map((e, i) => {
                const tm = TYPE_META[e.type] || { icon: Clock, cls: 'text-slate-400' };
                const Icon = tm.icon;
                return (
                  <li key={i} className="mb-4 ml-4" data-testid={`timeline-event-${i}`}>
                    <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-white border border-slate-300">
                      <Icon size={9} className={tm.cls} />
                    </span>
                    <div className="text-[10px] text-slate-500">{(e.ts || '').slice(0, 19).replace('T', ' ')}</div>
                    <div className="text-sm font-bold text-slate-900">{e.label}</div>
                    {e.summary && <div className="text-xs text-slate-600 mt-0.5">{e.summary}</div>}
                    {e.actor && <div className="text-[10px] text-slate-400 mt-0.5">by {e.actor}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
